import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Trash, X } from '@phosphor-icons/react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthenticatedMedia } from '../../lib/useAuthenticatedMedia';
import { CapsuleMessage } from './SecureCapsule';

const MediaAttachment = ({ mediaId }) => {
  const { blobUrl, error } = useAuthenticatedMedia(mediaId);
  const [open, setOpen] = useState(false);

  // Escape closes it. Registered unconditionally so the hook order never
  // changes between the loading, error and loaded renders below.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (error) {
    return (
      <div className="text-xs text-destructive mb-2">
        Failed to load media
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div className="w-40 h-32 bg-muted rounded-lg mb-2 animate-pulse" />
    );
  }
  return (
    <>
      {/* A thumbnail is a promise that the full picture is one tap away.
          Without this the 300px preview was all a member ever got. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block mb-2 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="View this image full size"
        data-testid={`media-open-${mediaId}`}
      >
        <img
          src={blobUrl}
          alt="Attachment"
          className="max-w-full"
          style={{ maxHeight: '300px' }}
          data-testid={`media-${mediaId}`}
        />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Image"
          data-testid={`media-viewer-${mediaId}`}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            aria-label="Close"
            data-testid={`media-close-${mediaId}`}
          >
            <X className="w-7 h-7" />
          </button>
          {/* Stop the image itself dismissing the viewer, so a member can
              pinch and pan it without it vanishing under their fingers. */}
          <img
            src={blobUrl}
            alt="Attachment, full size"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ message }) => {
  const { user } = useAuth();
  const { deleteMessage } = useSocket();
  const isSent = message.sender_id === user?.id;

  const handleDelete = () => {
    if (window.confirm('Delete this message?')) {
      deleteMessage(message.id);
    }
  };

  const senderName = message.sender_name || 'Deleted User';
  const isCapsule = message.message_type === 'capsule';

  return (
    <div
      className={`flex ${isSent ? 'justify-end' : 'justify-start'} mb-4 group`}
      data-testid={`message-${message.id}`}
    >
      {/* min-w-0 lets the column shrink below its content's intrinsic width;
          without it a long unbroken run (a URL, a shell prompt, a capsule
          code) forces the bubble wider than the phone screen. */}
      <div className={`max-w-[75%] md:max-w-[65%] min-w-0 ${isSent ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isSent && (
          <span className="text-xs text-muted-foreground mb-1 px-1" data-testid={`sender-${message.id}`}>
            {senderName}
          </span>
        )}

        {isCapsule ? (
          <CapsuleMessage message={message} />
        ) : (
        <div
          className={`
            rounded-2xl px-4 py-2.5 break-words border bg-card text-foreground
            ${isSent ? 'rounded-br-sm' : 'border-border rounded-bl-sm'}
            ${message.is_deleted ? 'opacity-50 italic' : ''}
          `}
          // Own messages share the calm card colour but carry a hairline gold
          // edge — a quiet "this is me" cue that stays legible in group chats.
          style={isSent ? { borderColor: 'hsl(var(--gold) / 0.55)' } : undefined}
        >
          {message.media_id && !message.is_deleted && (
            <MediaAttachment mediaId={message.media_id} />
          )}

          {/* `anywhere` rather than break-word: only `anywhere` shrinks the
              element's intrinsic min-content width, which is what actually
              stops a long unbroken run (URL, capsule code, shell prompt) from
              pushing the bubble wider than a phone screen. Inline because
              Tailwind does not emit this arbitrary property here. */}
          {(message.content || !message.media_id) && (
            <p
              className="text-sm leading-relaxed whitespace-pre-wrap"
              style={{ overflowWrap: 'anywhere' }}
            >
              {message.content || '[Media]'}
            </p>
          )}
        </div>
        )}

        <div className="flex items-center gap-2 mt-1 px-1">
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
          </span>

          {isSent && !message.is_deleted && (
            <button
              onClick={handleDelete}
              className="-my-2 flex h-10 w-10 items-center justify-center text-muted-foreground/70 hover:text-destructive"
              data-testid={`delete-message-${message.id}`}
              aria-label="Delete message"
            >
              <Trash className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {message.expires_at && !message.is_deleted && (
          <span className="text-xs text-secondary mt-1 px-1">
            Expires {formatDistanceToNow(new Date(message.expires_at), { addSuffix: true })}
          </span>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
